/*
 * Project: bellcloud
 * File: packages/ui/components/common/ButtonTimeLine.tsx
 * Description: Timeline control buttons for playback controls
 */

import styled from '@emotion/styled';
import type { AppTheme } from '@/theme/theme';

type ButtonTimeLineType = 'pause' | 'play' | 'speed' | 'backward' | 'forward';
type SpeedValue = 0.5 | 1 | 2 | 4;

interface ButtonTimeLineProps {
  type: ButtonTimeLineType;
  speedValue?: SpeedValue;
  onClick?: () => void;
  disabled?: boolean;
}

const StyledTimeLineButton = styled.button`
  display: inline-flex;
  padding: 4px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${({ theme }) => (theme as AppTheme).colors.control.splitBar.default};
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme as AppTheme).colors.text.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => (theme as AppTheme).colors.border.shadow.md};
  }
`;

const IconWrapper = styled.span<{ $iconPath: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  aspect-ratio: 1/1;
  background-color: ${({ theme }) => (theme as AppTheme).colors.text.primary};
  mask-image: url(${({ $iconPath }) => $iconPath});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url(${({ $iconPath }) => $iconPath});
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

const SpeedText = styled.span`
  color: ${({ theme }) => (theme as AppTheme).colors.text.primary};
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
`;

const getIconPath = (type: ButtonTimeLineType): string => {
  const iconMap: Record<Exclude<ButtonTimeLineType, 'speed'>, string> = {
    pause: '/icons/pause.svg',
    play: '/icons/play.svg',
    backward: '/icons/backward.svg',
    forward: '/icons/forward.svg',
  };
  return iconMap[type as Exclude<ButtonTimeLineType, 'speed'>];
};

export const ButtonTimeLine = ({
  type,
  speedValue = 1,
  onClick,
  disabled = false,
}: ButtonTimeLineProps) => {
  return (
    <StyledTimeLineButton onClick={onClick} disabled={disabled}>
      {type === 'speed' ? (
        <SpeedText>X{speedValue}</SpeedText>
      ) : (
        <IconWrapper $iconPath={getIconPath(type)} />
      )}
    </StyledTimeLineButton>
  );
};

export const ButtonTimeLineGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
